import {useCallback, useEffect, useState} from "react";
import {FormProvider} from "react-hook-form";
import {css} from "@panda/css";

import {InputField} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";
import {phoneNumberFormat} from "@pg-mono/string-utils";
import {digitFields, getTimeInMinutes, usePhoneVerificationCode, useUserPhoneVerificationContext} from "@pg-mono/user";

import {UserPhoneVerificationProvider} from "@/features/user/providers/UserPhoneVerificationProvider";
import {unstyledButton} from "@/styles/receipes/unstyled_button";

import {
    getHomeMatchPreferencesWizardSmsDigitLabel,
    HOME_MATCH_PREFERENCES_WIZARD_CONTENT
} from "../../constants/preferences/home_match_preferences_wizard_content";
import {useHomeMatchPreferencesWizardSmsDigits} from "../../hooks/preferences/use_home_match_preferences_wizard_sms_digits";

const {SMS} = HOME_MATCH_PREFERENCES_WIZARD_CONTENT;
const HTTP_TOO_MANY_REQUESTS = 429;

interface IProps {
    onCanConfirmChange: (canConfirm: boolean) => void;
    onRegisterSubmit: (submit: (() => Promise<void>) | null) => void;
    onResend?: () => void;
    onSubmittingChange: (isSubmitting: boolean) => void;
    onSuccess: () => void;
}

export const HomeMatchPreferencesWizardSmsStep = (props: IProps) => {
    return (
        <UserPhoneVerificationProvider>
            <SmsCodeFields {...props} />
        </UserPhoneVerificationProvider>
    );
};

const SmsCodeFields = (props: IProps) => {
    const {onCanConfirmChange, onRegisterSubmit, onResend, onSubmittingChange, onSuccess} = props;
    const {error, isVerifying: isSubmitting, resendCode: resendVerificationCode, timer, verifyCode: verifyPhoneCode} = usePhoneVerificationCode();
    const {hotlinePhoneNumber} = useUserPhoneVerificationContext();
    const {canConfirmCode, changeDigit, digitNavigation, form, getCode, inputRefs, pasteCode} = useHomeMatchPreferencesWizardSmsDigits();
    const [codeError, setCodeError] = useState<string | null>(null);

    const verifyCode = useCallback(async () => {
        const code = getCode();

        if (!/^\d{4}$/.test(code)) {
            setCodeError(SMS.CODE_INVALID);
            return;
        }

        setCodeError(null);
        await verifyPhoneCode(code, onSuccess);
    }, [getCode, onSuccess, verifyPhoneCode]);

    useEffect(() => {
        onSubmittingChange(isSubmitting);
    }, [isSubmitting, onSubmittingChange]);

    useEffect(() => {
        onCanConfirmChange(canConfirmCode);
    }, [canConfirmCode, onCanConfirmChange]);

    useEffect(() => {
        return () => onCanConfirmChange(false);
    }, [onCanConfirmChange]);

    useEffect(() => {
        onRegisterSubmit(verifyCode);

        return () => onRegisterSubmit(null);
    }, [onRegisterSubmit, verifyCode]);

    const resendCode = () => {
        if (timer > 0 || isSubmitting) return;

        onResend?.();
        setCodeError(null);
        resendVerificationCode();
    };

    const formError = codeError ?? error?.message;
    const isTooManyRequests = error?.status === HTTP_TOO_MANY_REQUESTS;

    return (
        <div className={stepStyle}>
            <FormProvider {...form}>
                <form
                    onSubmit={(event) => {
                        event.preventDefault();
                        void verifyCode();
                    }}
                >
                    <div aria-label={SMS.DESCRIPTION} className={digitsStyle} data-sms-code="" role="group">
                        {digitFields.map((fieldName, index) => (
                            <InputField
                                key={fieldName}
                                ref={(element) => {
                                    inputRefs.current[index] = element;
                                }}
                                aria-label={getHomeMatchPreferencesWizardSmsDigitLabel(index + 1)}
                                autoComplete={index === 0 ? "one-time-code" : "off"}
                                className={digitStyle}
                                inputMode="numeric"
                                name={fieldName}
                                noPadding
                                onChange={(event) => {
                                    changeDigit(fieldName, event.target.value);

                                    if (event.target.value.replace(/\D/g, "").length > 1) event.target.value = event.target.value.replace(/\D/g, "").slice(-1);
                                }}
                                onKeyDown={(event) => {
                                    if (event.key === "Backspace" && !(event.target as HTMLInputElement).value) digitNavigation.moveToPreviousField(fieldName);
                                }}
                                onPaste={(event) => {
                                    event.preventDefault();
                                    pasteCode(event.clipboardData.getData("text"));
                                }}
                            />
                        ))}
                    </div>
                </form>
            </FormProvider>
            <div className={errorSlotStyle}>
                {formError && (
                    <Text className={errorStyle} color="var(--colors-danger)" preserveFontSize variant="info_txt_1">
                        {formError}
                    </Text>
                )}
            </div>
            {isTooManyRequests ? (
                <Text align="center" color="var(--colors-gray-700)" preserveFontSize variant="info_txt_1">
                    {SMS.NO_CODE}
                    {hotlinePhoneNumber && (
                        <>
                            <br />
                            {SMS.CONTACT_OFFICE}{" "}
                            <a href={`tel:+${hotlinePhoneNumber.replace(/\D/g, "")}`}>
                                <Text as="span" color="var(--colors-gray-700)" preserveFontSize strong variant="info_txt_1">
                                    {phoneNumberFormat(hotlinePhoneNumber)}
                                </Text>
                            </a>
                        </>
                    )}
                </Text>
            ) : (
                <button className={resendStyle} disabled={timer > 0} onClick={resendCode} type="button">
                    <Text preserveFontSize variant="info_txt_1">
                        {timer > 0 ? `${SMS.RESEND_WAIT} ${getTimeInMinutes(timer)}` : SMS.RESEND}
                    </Text>
                </button>
            )}
        </div>
    );
};

const stepStyle = css({
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    gap: 2
});

const digitsStyle = css({
    display: "flex",
    gap: 0.5,
    justifyContent: "center",

    "&:focus-within > * > div > div": {
        height: "8rem"
    },

    md: {
        gap: 1,

        "&:focus-within > * > div > div": {
            height: "10rem"
        }
    }
});

const digitStyle = css({
    "& > div > div": {
        alignItems: "center",
        border: "1px solid var(--colors-gray-900)",
        height: "10rem",
        width: "8.1rem"
    },
    "& input": {
        fontSize: "3.2rem",
        textAlign: "center"
    },
    width: "8.1rem",

    md: {
        width: "11.7rem",

        "& > div > div": {
            width: "11.7rem"
        }
    }
});

const errorSlotStyle = css({
    alignSelf: "stretch",
    height: "1.6rem",
    position: "relative"
});

const errorStyle = css({
    left: 0,
    position: "absolute",
    right: 0,
    textAlign: "left",
    top: 0,

    md: {
        textAlign: "center"
    }
});

const resendStyle = css(unstyledButton, {
    alignSelf: "center",
    color: "var(--colors-gray-700)",
    textAlign: "center",

    _disabled: {
        cursor: "default"
    }
});
import {useCallback, useEffect, useState} from "react";
import {FormProvider} from "react-hook-form";
import {css} from "@panda/css";

import {InputField} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";
import {phoneNumberFormat} from "@pg-mono/string-utils";
import {digitFields, getTimeInMinutes, usePhoneVerificationCode, useUserPhoneVerificationContext} from "@pg-mono/user";

import {UserPhoneVerificationProvider} from "@/features/user/providers/UserPhoneVerificationProvider";
import {unstyledButton} from "@/styles/receipes/unstyled_button";

import {
    getHomeMatchPreferencesWizardSmsDigitLabel,
    HOME_MATCH_PREFERENCES_WIZARD_CONTENT
} from "../../constants/preferences/home_match_preferences_wizard_content";
import {useHomeMatchPreferencesWizardSmsDigits} from "../../hooks/preferences/use_home_match_preferences_wizard_sms_digits";

const {SMS} = HOME_MATCH_PREFERENCES_WIZARD_CONTENT;
const HTTP_TOO_MANY_REQUESTS = 429;

interface IProps {
    onCanConfirmChange: (canConfirm: boolean) => void;
    onRegisterSubmit: (submit: (() => Promise<void>) | null) => void;
    onResend?: () => void;
    onSubmittingChange: (isSubmitting: boolean) => void;
    onSuccess: () => void;
}

export const HomeMatchPreferencesWizardSmsStep = (props: IProps) => {
    return (
        <UserPhoneVerificationProvider>
            <SmsCodeFields {...props} />
        </UserPhoneVerificationProvider>
    );
};

const SmsCodeFields = (props: IProps) => {
    const {onCanConfirmChange, onRegisterSubmit, onResend, onSubmittingChange, onSuccess} = props;
    const {error, isVerifying: isSubmitting, resendCode: resendVerificationCode, timer, verifyCode: verifyPhoneCode} = usePhoneVerificationCode();
    const {hotlinePhoneNumber} = useUserPhoneVerificationContext();
    const {canConfirmCode, changeDigit, digitNavigation, form, getCode, inputRefs, pasteCode} = useHomeMatchPreferencesWizardSmsDigits();
    const [codeError, setCodeError] = useState<string | null>(null);

    const verifyCode = useCallback(async () => {
        const code = getCode();

        if (!/^\d{4}$/.test(code)) {
            setCodeError(SMS.CODE_INVALID);
            return;
        }

        setCodeError(null);
        await verifyPhoneCode(code, onSuccess);
    }, [getCode, onSuccess, verifyPhoneCode]);

    useEffect(() => {
        onSubmittingChange(isSubmitting);
    }, [isSubmitting, onSubmittingChange]);

    useEffect(() => {
        onCanConfirmChange(canConfirmCode);
    }, [canConfirmCode, onCanConfirmChange]);

    useEffect(() => {
        return () => onCanConfirmChange(false);
    }, [onCanConfirmChange]);

    useEffect(() => {
        onRegisterSubmit(verifyCode);

        return () => onRegisterSubmit(null);
    }, [onRegisterSubmit, verifyCode]);

    const resendCode = () => {
        if (timer > 0 || isSubmitting) return;

        onResend?.();
        setCodeError(null);
        resendVerificationCode();
    };

    const formError = codeError ?? error?.message;
    const isTooManyRequests = error?.status === HTTP_TOO_MANY_REQUESTS;

    return (
        <div className={stepStyle}>
            <FormProvider {...form}>
                <form
                    onSubmit={(event) => {
                        event.preventDefault();
                        void verifyCode();
                    }}
                >
                    <div aria-label={SMS.DESCRIPTION} className={digitsStyle} data-sms-code="" role="group">
                        {digitFields.map((fieldName, index) => (
                            <InputField
                                key={fieldName}
                                ref={(element) => {
                                    inputRefs.current[index] = element;
                                }}
                                aria-label={getHomeMatchPreferencesWizardSmsDigitLabel(index + 1)}
                                autoComplete={index === 0 ? "one-time-code" : "off"}
                                className={digitStyle}
                                inputMode="numeric"
                                name={fieldName}
                                noPadding
                                onChange={(event) => {
                                    changeDigit(fieldName, event.target.value);

                                    if (event.target.value.replace(/\D/g, "").length > 1) event.target.value = event.target.value.replace(/\D/g, "").slice(-1);
                                }}
                                onKeyDown={(event) => {
                                    if (event.key === "Backspace" && !(event.target as HTMLInputElement).value) digitNavigation.moveToPreviousField(fieldName);
                                }}
                                onPaste={(event) => {
                                    event.preventDefault();
                                    pasteCode(event.clipboardData.getData("text"));
                                }}
                            />
                        ))}
                    </div>
                </form>
            </FormProvider>
            <div className={errorSlotStyle}>
                {formError && (
                    <Text className={errorStyle} color="var(--colors-danger)" preserveFontSize variant="info_txt_1">
                        {formError}
                    </Text>
                )}
            </div>
            {isTooManyRequests ? (
                <Text align="center" color="var(--colors-gray-700)" preserveFontSize variant="info_txt_1">
                    {SMS.NO_CODE}
                    {hotlinePhoneNumber && (
                        <>
                            <br />
                            {SMS.CONTACT_OFFICE}{" "}
                            <a href={`tel:+${hotlinePhoneNumber.replace(/\D/g, "")}`}>
                                <Text as="span" color="var(--colors-gray-700)" preserveFontSize strong variant="info_txt_1">
                                    {phoneNumberFormat(hotlinePhoneNumber)}
                                </Text>
                            </a>
                        </>
                    )}
                </Text>
            ) : (
                <button className={resendStyle} disabled={timer > 0} onClick={resendCode} type="button">
                    <Text preserveFontSize variant="info_txt_1">
                        {timer > 0 ? `${SMS.RESEND_WAIT} ${getTimeInMinutes(timer)}` : SMS.RESEND}
                    </Text>
                </button>
            )}
        </div>
    );
};

const stepStyle = css({
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    gap: 2
});

const digitsStyle = css({
    display: "flex",
    gap: 0.5,
    justifyContent: "center",

    "&:focus-within > * > div > div": {
        height: "8rem"
    },

    md: {
        gap: 1,

        "&:focus-within > * > div > div": {
            height: "10rem"
        }
    }
});

const digitStyle = css({
    "& > div > div": {
        alignItems: "center",
        border: "1px solid var(--colors-gray-900)",
        height: "10rem",
        width: "8.1rem"
    },
    "& input": {
        fontSize: "3.2rem",
        textAlign: "center"
    },
    width: "8.1rem",

    md: {
        width: "11.7rem",

        "& > div > div": {
            width: "11.7rem"
        }
    }
});

const errorSlotStyle = css({
    alignSelf: "stretch",
    height: "1.6rem",
    position: "relative"
});

const errorStyle = css({
    left: 0,
    position: "absolute",
    right: 0,
    textAlign: "left",
    top: 0,

    md: {
        textAlign: "center"
    }
});

const resendStyle = css(unstyledButton, {
    alignSelf: "center",
    color: "var(--colors-gray-700)",
    textAlign: "center",

    _disabled: {
        cursor: "default"
    }
});
